import React, { useState, useEffect, useMemo, useCallback } from "react";
import { toast } from "react-toastify";
import {
  HiOutlineArrowLeft,
  HiOutlineMagnifyingGlass,
  HiOutlineSquares2X2,
  HiOutlinePhoto,
  HiOutlineTrash,
} from "react-icons/hi2";
import { adminService } from "../../services";
import { APP_CATEGORIES } from "../../data/maxAppsDefinitions";
import { getWorkspaceOverviewCatalog } from "../../navigation/workspaceOverviewCatalog";
import { isNavKeyExemptFromAppScope } from "../../navigation/navAppExemptKeys";
import {
  isLegacyNavAppHiddenFromTenantScope,
  normalizeTenantNavAppCodes,
} from "../../navigation/legacyNavAppScope";
import { SETTINGS_MENU_ITEMS_RAW } from "../../navigation/navConfig";
import PageLoading from "../../components/common/PageLoading";
import "./ManageFeatureApps.css";

/** Keys always visible for every tenant — never part of an app bundle. */
function filterNavKeysForAppDefinition(keys) {
  if (!keys?.length) return [];
  return keys.filter((k) => !isNavKeyExemptFromAppScope(k));
}

const TAB_DEFS = "defs";
const TAB_TENANT = "tenant";

const emptyForm = () => ({
  code: "",
  name: "",
  description: "",
  displayOrder: 0,
  isActive: true,
  navKeys: [],
  iconEmoji: "📦",
  colorHex: "#4F46E5",
  category: "Production",
  version: "1.0.0",
  featuresJson: "[]",
  screenshotsJson: "[]",
  releaseNotes: "",
});

const ManageFeatureApps = () => {
  /** App bundles only scope module pages; Home, launcher & Settings are exempt. */
  const appDefinitionCatalog = useMemo(
    () =>
      getWorkspaceOverviewCatalog().filter(
        (row) => !isNavKeyExemptFromAppScope(row.id),
      ),
    [],
  );
  const [tab, setTab] = useState(TAB_DEFS);

  const [apps, setApps] = useState([]);
  const [loadingApps, setLoadingApps] = useState(true);

  const [tenants, setTenants] = useState([]);
  const [tenantId, setTenantId] = useState("");
  /** inherit = API null (no app filter). custom = explicit app list (may be empty). */
  const [tenantScopeMode, setTenantScopeMode] = useState("inherit");
  const [selectedCodes, setSelectedCodes] = useState(new Set());
  const [loadingTenant, setLoadingTenant] = useState(false);

  const [editorOpen, setEditorOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [pageSearch, setPageSearch] = useState("");

  const loadApps = async () => {
    try {
      setLoadingApps(true);
      const data = await adminService.getNavApps();
      setApps(Array.isArray(data) ? data : []);
    } catch (e) {
      toast.error(e.response?.data?.message || "Failed to load nav apps");
    } finally {
      setLoadingApps(false);
    }
  };

  useEffect(() => {
    loadApps();
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await adminService.getAllTenants();
        if (!cancelled) setTenants(data || []);
      } catch (e) {
        if (!cancelled) toast.error("Failed to load tenants");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!tenantId) {
      setSelectedCodes(new Set());
      setTenantScopeMode("inherit");
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        setLoadingTenant(true);
        const data = await adminService.getTenantNavApps(tenantId);
        const codes = data?.appCodes;
        if (!cancelled) {
          if (codes == null) {
            setTenantScopeMode("inherit");
            setSelectedCodes(new Set());
          } else {
            setTenantScopeMode("custom");
            setSelectedCodes(new Set(normalizeTenantNavAppCodes(codes)));
          }
        }
      } catch (e) {
        if (!cancelled) {
          toast.error(e.response?.data?.message || "Failed to load tenant apps");
          setSelectedCodes(new Set());
          setTenantScopeMode("inherit");
        }
      } finally {
        if (!cancelled) setLoadingTenant(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [tenantId]);

  const closeEditor = useCallback(() => {
    setEditorOpen(false);
    setEditingId(null);
    setForm(emptyForm());
    setPageSearch("");
  }, []);

  useEffect(() => {
    if (!editorOpen) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") closeEditor();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [editorOpen, closeEditor]);

  const openCreate = () => {
    setEditingId(null);
    setForm(emptyForm());
    setPageSearch("");
    setEditorOpen(true);
  };

  const openEdit = (row) => {
    setEditingId(row.navAppId);
    setForm({
      code: row.code || "",
      name: row.name || "",
      description: row.description || "",
      displayOrder: row.displayOrder ?? 0,
      isActive: row.isActive !== false,
      navKeys: filterNavKeysForAppDefinition(row.navKeys),
      iconEmoji: row.iconEmoji || "📦",
      colorHex: row.colorHex || "#4F46E5",
      category: row.category || "Production",
      version: row.version || "1.0.0",
      featuresJson: row.features ? JSON.stringify(row.features) : (row.featuresJson || "[]"),
      screenshotsJson: row.screenshots ? JSON.stringify(row.screenshots) : (row.screenshotsJson || "[]"),
      releaseNotes: row.releaseNotes || "",
    });
    setPageSearch("");
    setEditorOpen(true);
  };

  const toggleKey = (key) => {
    setForm((f) => {
      const set = new Set(f.navKeys);
      if (set.has(key)) set.delete(key);
      else set.add(key);
      return { ...f, navKeys: [...set] };
    });
  };

  const toggleGroupKeys = (itemIds, selectAll) => {
    setForm((f) => {
      const next = new Set(f.navKeys);
      for (const id of itemIds) {
        if (selectAll) next.add(id);
        else next.delete(id);
      }
      return { ...f, navKeys: [...next] };
    });
  };

  const groupedCatalog = useMemo(() => {
    const q = pageSearch.trim().toLowerCase();
    const byGroup = new Map();
    for (const c of appDefinitionCatalog) {
      if (q) {
        const hay = `${c.label} ${c.id} ${c.group}`.toLowerCase();
        if (!hay.includes(q)) continue;
      }
      if (!byGroup.has(c.group)) byGroup.set(c.group, []);
      byGroup.get(c.group).push(c);
    }
    return [...byGroup.entries()].sort((a, b) =>
      a[0].localeCompare(b[0], undefined, { sensitivity: "base" }),
    );
  }, [appDefinitionCatalog, pageSearch]);

  const selectedCount = form.navKeys.length;

  const saveApp = async () => {
    if (!form.name?.trim()) {
      toast.error("Name is required");
      return;
    }
    if (!editingId && !form.code?.trim()) {
      toast.error("Code is required");
      return;
    }
    try {
      setSaving(true);
      const payload = {
        code: form.code?.trim(),
        name: form.name.trim(),
        description: form.description?.trim() || null,
        displayOrder: Number(form.displayOrder) || 0,
        isActive: !!form.isActive,
        navKeys: filterNavKeysForAppDefinition(form.navKeys),
        iconEmoji: form.iconEmoji,
        colorHex: form.colorHex,
        category: form.category,
        version: form.version,
        featuresJson: form.featuresJson,
        screenshotsJson: form.screenshotsJson,
        releaseNotes: form.releaseNotes,
      };
      if (editingId) {
        await adminService.updateNavApp(editingId, payload);
        toast.success("App updated");
      } else {
        await adminService.createNavApp(payload);
        toast.success("App created");
      }
      closeEditor();
      loadApps();
    } catch (e) {
      toast.error(e.response?.data?.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const deactivate = async (row) => {
    if (
      !window.confirm(
        `Deactivate "${row.name}"? Tenants referencing it will no longer receive those pages from this app.`,
      )
    ) {
      return;
    }
    try {
      await adminService.deactivateNavApp(row.navAppId);
      toast.success("App deactivated");
      loadApps();
    } catch (e) {
      toast.error(e.response?.data?.message || "Failed");
    }
  };

  const saveTenantApps = async () => {
    if (!tenantId) return;
    try {
      setSaving(true);
      if (tenantScopeMode === "inherit") {
        await adminService.updateTenantNavApps(tenantId, { appCodes: null });
      } else {
        await adminService.updateTenantNavApps(tenantId, {
          appCodes: [...selectedCodes],
        });
      }
      toast.success("Tenant feature apps saved");
    } catch (e) {
      toast.error(e.response?.data?.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const clearTenantApps = async () => {
    if (!tenantId) return;
    if (
      !window.confirm(
        "Clear app assignment? The tenant will see all licensed modules (no app-based filtering).",
      )
    ) {
      return;
    }
    try {
      setSaving(true);
      await adminService.updateTenantNavApps(tenantId, { appCodes: null });
      setSelectedCodes(new Set());
      setTenantScopeMode("inherit");
      toast.success("Cleared — tenant uses default module visibility");
    } catch (e) {
      toast.error(e.response?.data?.message || "Failed");
    } finally {
      setSaving(false);
    }
  };

  const toggleTenantCode = (code) => {
    setSelectedCodes((prev) => {
      const next = new Set(prev);
      if (next.has(code)) next.delete(code);
      else next.add(code);
      return next;
    });
  };

  const activeApps = apps.filter(
    (a) => a.isActive && !isLegacyNavAppHiddenFromTenantScope(a.code),
  );

  const settingsPageCount = SETTINGS_MENU_ITEMS_RAW.length;

  const editorTitle = editingId ? "Edit app" : "Create app";

  return (
    <div
      className={`manage-feature-apps ${editorOpen ? "manage-feature-apps--editor" : ""}`}
    >
      {!editorOpen && (
        <>
          <h2>Feature apps</h2>
          <p className="lead">
            Group pages into named <strong>Apps</strong> (feature sets), then optionally
            assign apps per tenant. <strong>Home</strong>, <strong>All modules</strong>, and{" "}
            <strong>all Settings pages</strong> are always on for every tenant (they are not
            controlled by app assignment). Subscription features and permissions still apply.
          </p>

          <div className="mfa-tabs">
            <button
              type="button"
              className={tab === TAB_DEFS ? "active" : ""}
              onClick={() => setTab(TAB_DEFS)}
            >
              App definitions
            </button>
            <button
              type="button"
              className={tab === TAB_TENANT ? "active" : ""}
              onClick={() => setTab(TAB_TENANT)}
            >
              Assign to tenants
            </button>
          </div>
        </>
      )}

      {tab === TAB_DEFS && !editorOpen && (
        <>
          <div className="mfa-toolbar">
            <button type="button" className="btn-mfa-primary" onClick={openCreate}>
              New app
            </button>
          </div>
          {loadingApps ? (
            <PageLoading variant="compact" message="Loading apps…" />
          ) : (
            <div className="mfa-table-wrap">
              <table className="mfa-table">
                <thead>
                  <tr>
                    <th>Order</th>
                    <th>Code</th>
                    <th>Name</th>
                    <th>Pages</th>
                    <th>Active</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {apps.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="mfa-muted">
                        No apps yet. Create one and add page keys from the catalog.
                      </td>
                    </tr>
                  ) : (
                    apps.map((a) => (
                      <tr key={a.navAppId}>
                        <td>{a.displayOrder}</td>
                        <td>
                          <code>{a.code}</code>
                        </td>
                        <td>{a.name}</td>
                        <td>{(a.navKeys || []).length}</td>
                        <td>{a.isActive ? "Yes" : "No"}</td>
                        <td>
                          <button
                            type="button"
                            className="btn-mfa-secondary"
                            onClick={() => openEdit(a)}
                          >
                            Edit
                          </button>{" "}
                          {a.isActive && (
                            <button
                              type="button"
                              className="btn-mfa-danger"
                              onClick={() => deactivate(a)}
                            >
                              Deactivate
                            </button>
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {tab === TAB_DEFS && editorOpen && (
        <div className="mfa-editor">
          <header className="mfa-editor-topbar">
            <button
              type="button"
              className="mfa-editor-back"
              onClick={closeEditor}
              disabled={saving}
            >
              <HiOutlineArrowLeft aria-hidden />
              <span>Back to list</span>
            </button>
            <div className="mfa-editor-topbar-center">
              <HiOutlineSquares2X2 className="mfa-editor-title-icon" aria-hidden />
              <div>
                <h2 className="mfa-editor-title">{editorTitle}</h2>
                <p className="mfa-editor-sub">
                  {editingId
                    ? "Update details and which workspace pages belong to this app."
                    : "Choose a unique code, a clear name, then pick the pages to include."}
                </p>
              </div>
            </div>
            <div className="mfa-editor-topbar-actions">
              <button
                type="button"
                className="btn-mfa-secondary"
                onClick={closeEditor}
                disabled={saving}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn-mfa-primary"
                onClick={saveApp}
                disabled={saving}
              >
                {saving ? "Saving…" : editingId ? "Save changes" : "Create app"}
              </button>
            </div>
          </header>

          <div className="mfa-editor-grid">
            <section className="mfa-editor-panel" aria-labelledby="mfa-details-heading">
              <h3 id="mfa-details-heading" className="mfa-panel-heading">
                App details
              </h3>
              {!editingId && (
                <div className="mfa-field">
                  <label htmlFor="mfa-code">Code</label>
                  <input
                    id="mfa-code"
                    value={form.code}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, code: e.target.value }))
                    }
                    placeholder="e.g. OEE_SUITE"
                    autoComplete="off"
                    spellCheck={false}
                  />
                  <p className="mfa-field-hint">
                    Short unique ID (letters, numbers, underscores). Cannot be changed later.
                  </p>
                </div>
              )}
              {editingId && (
                <div className="mfa-field mfa-field-readonly">
                  <span className="mfa-label">Code</span>
                  <code className="mfa-code-readonly">{form.code}</code>
                </div>
              )}
              <div className="mfa-field">
                <label htmlFor="mfa-name">Display name</label>
                <input
                  id="mfa-name"
                  value={form.name}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, name: e.target.value }))
                  }
                  placeholder="Name shown in admin and assignments"
                />
              </div>
              <div className="mfa-field">
                <label htmlFor="mfa-desc">Description (optional)</label>
                <textarea
                  id="mfa-desc"
                  rows={3}
                  value={form.description}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, description: e.target.value }))
                  }
                  placeholder="What this app is for — helps other admins choose the right bundle."
                />
              </div>
              <div className="mfa-field-row">
                <div className="mfa-field">
                  <label htmlFor="mfa-order">Display order</label>
                  <input
                    id="mfa-order"
                    type="number"
                    value={form.displayOrder}
                    onChange={(e) =>
                      setForm((f) => ({
                        ...f,
                        displayOrder: parseInt(e.target.value, 10) || 0,
                      }))
                    }
                  />
                </div>
                {editingId && (
                  <div className="mfa-field mfa-field-checkbox">
                    <label htmlFor="mfa-active">
                      <input
                        id="mfa-active"
                        type="checkbox"
                        checked={form.isActive}
                        onChange={(e) =>
                          setForm((f) => ({ ...f, isActive: e.target.checked }))
                        }
                      />
                      Active
                    </label>
                  </div>
                )}
              </div>

              <div className="mfa-field-row">
                <div className="mfa-field">
                  <label htmlFor="mfa-emoji">Icon Emoji</label>
                  <div className="mfa-emoji-input-wrap">
                    <input
                      id="mfa-emoji"
                      value={form.iconEmoji}
                      onChange={(e) => setForm(f => ({ ...f, iconEmoji: e.target.value }))}
                      placeholder="Emoji code (e.g. 🛠️)"
                    />
                    <span className="mfa-emoji-preview">{form.iconEmoji}</span>
                  </div>
                </div>
                <div className="mfa-field">
                  <label htmlFor="mfa-color">Accent Color</label>
                  <div className="mfa-color-input-wrap">
                    <input
                      id="mfa-color"
                      type="color"
                      value={form.colorHex}
                      onChange={(e) => setForm(f => ({ ...f, colorHex: e.target.value }))}
                    />
                    <code>{form.colorHex}</code>
                  </div>
                </div>
              </div>

              <div className="mfa-field-row">
                <div className="mfa-field">
                  <label htmlFor="mfa-cat">Category</label>
                  <select
                    id="mfa-cat"
                    value={form.category}
                    onChange={(e) => setForm(f => ({ ...f, category: e.target.value }))}
                  >
                    {APP_CATEGORIES.filter(c => c.value !== 'all').map(c => (
                      <option key={c.value} value={c.value}>{c.label}</option>
                    ))}
                  </select>
                </div>
                <div className="mfa-field">
                  <label htmlFor="mfa-ver">Version</label>
                  <input
                    id="mfa-ver"
                    value={form.version}
                    onChange={(e) => setForm(f => ({ ...f, version: e.target.value }))}
                    placeholder="e.g. 2.0.0"
                  />
                </div>
              </div>

              <div className="mfa-field">
                <label htmlFor="mfa-features">Features (One per line)</label>
                <textarea
                  id="mfa-features"
                  rows={5}
                  value={(() => {
                    try {
                      const arr = typeof form.featuresJson === 'string' ? JSON.parse(form.featuresJson) : form.featuresJson;
                      return Array.isArray(arr) ? arr.join('\n') : '';
                    } catch (e) { return ''; }
                  })()}
                  onChange={(e) => {
                    const lines = e.target.value.split('\n').filter(l => l.trim().length > 0);
                    setForm(f => ({ ...f, featuresJson: JSON.stringify(lines) }));
                  }}
                  placeholder="Feature 1&#10;Feature 2&#10;Feature 3"
                />
              </div>

              <div className="mfa-field">
                <label>Screenshots</label>
                <div className="mfa-screenshot-manager">
                  {(() => {
                    try {
                      const urls = typeof form.screenshotsJson === 'string' ? JSON.parse(form.screenshotsJson) : form.screenshotsJson;
                      return Array.isArray(urls) && urls.map((url, idx) => (
                        <div key={idx} className="mfa-screenshot-thumb">
                          <img src={url} alt="Screenshot" />
                          <button 
                            type="button" 
                            className="btn-mfa-danger-small"
                            onClick={() => {
                              const next = urls.filter((_, i) => i !== idx);
                              setForm(f => ({ ...f, screenshotsJson: JSON.stringify(next) }));
                            }}
                          >
                            <HiOutlineTrash />
                          </button>
                        </div>
                      ));
                    } catch (e) { return null; }
                  })()}
                  <label className="mfa-screenshot-upload">
                    <HiOutlinePhoto />
                    <span>Upload</span>
                    <input 
                      type="file" 
                      hidden 
                      onChange={async (e) => {
                        const file = e.target.files?.[0];
                        if (!file || !editingId) {
                          if (!editingId) toast.info("Save the app first before uploading screenshots");
                          return;
                        }
                        try {
                          toast.info("Uploading screenshot...");
                          const res = await adminService.uploadScreenshot(editingId, file);
                          const urls = JSON.parse(form.screenshotsJson || "[]");
                          urls.push(res.url);
                          setForm(f => ({ ...f, screenshotsJson: JSON.stringify(urls) }));
                          toast.success("Uploaded!");
                        } catch (err) {
                          toast.error("Upload failed");
                        }
                      }}
                    />
                  </label>
                </div>
              </div>

              <div className="mfa-field">
                <label htmlFor="mfa-notes">Release Notes (Markdown)</label>
                <textarea
                  id="mfa-notes"
                  rows={4}
                  value={form.releaseNotes}
                  onChange={(e) => setForm(f => ({ ...f, releaseNotes: e.target.value }))}
                  placeholder="Describe recent changes..."
                />
              </div>

              <div className="mfa-preview-section">
                <label>Live Card Preview</label>
                <div className="mfa-preview-card" style={{ '--accent-color': form.colorHex }}>
                  <div className="mfa-preview-icon" style={{ backgroundColor: `${form.colorHex}22` }}>
                    <span>{form.iconEmoji}</span>
                  </div>
                  <div className="mfa-preview-info">
                    <div className="mfa-preview-name">{form.name || "Untitled App"}</div>
                    <div className="mfa-preview-cat">{form.category}</div>
                  </div>
                </div>
              </div>
            </section>

            <section
              className="mfa-editor-panel mfa-editor-panel--pages"
              aria-labelledby="mfa-pages-heading"
            >
              <div className="mfa-pages-head">
                <div>
                  <h3 id="mfa-pages-heading" className="mfa-panel-heading">
                    Pages in this app
                  </h3>
                  <p className="mfa-panel-lead">
                    Choose which <strong>module</strong> pages belong in this bundle. Home,
                    the All modules launcher, and every Settings page are already on for all
                    tenants — they are not shown here and cannot be removed by app assignment.
                  </p>
                </div>
                <div className="mfa-pages-count" aria-live="polite">
                  <strong>{selectedCount}</strong>
                  <span> selected</span>
                </div>
              </div>

              <div className="mfa-page-search">
                <HiOutlineMagnifyingGlass className="mfa-page-search-icon" aria-hidden />
                <input
                  type="search"
                  value={pageSearch}
                  onChange={(e) => setPageSearch(e.target.value)}
                  placeholder="Filter by page name, area, or key…"
                  aria-label="Filter pages"
                />
                {pageSearch && (
                  <button
                    type="button"
                    className="mfa-page-search-clear"
                    onClick={() => setPageSearch("")}
                  >
                    Clear
                  </button>
                )}
              </div>

              {groupedCatalog.length === 0 ? (
                <p className="mfa-muted mfa-pages-empty">
                  No pages match your filter. Try a different search.
                </p>
              ) : (
                <div className="mfa-page-groups">
                  {groupedCatalog.map(([group, items]) => {
                    const ids = items.map((i) => i.id);
                    const selectedHere = ids.filter((id) =>
                      form.navKeys.includes(id),
                    ).length;
                    const allSelected =
                      ids.length > 0 && selectedHere === ids.length;
                    const noneSelected = selectedHere === 0;
                    return (
                      <details key={group} className="mfa-key-group" open>
                        <summary className="mfa-key-group-summary">
                          <span className="mfa-key-group-title">{group}</span>
                          <span className="mfa-key-group-meta">
                            {selectedHere}/{items.length} in group
                          </span>
                        </summary>
                        <div className="mfa-key-group-toolbar">
                          <button
                            type="button"
                            className="mfa-key-group-btn"
                            disabled={allSelected}
                            onClick={() => toggleGroupKeys(ids, true)}
                          >
                            Select all in group
                          </button>
                          <button
                            type="button"
                            className="mfa-key-group-btn"
                            disabled={noneSelected}
                            onClick={() => toggleGroupKeys(ids, false)}
                          >
                            Clear group
                          </button>
                        </div>
                        <ul className="mfa-key-list">
                          {items.map((c) => {
                            const checked = form.navKeys.includes(c.id);
                            return (
                              <li key={c.id}>
                                <label className="mfa-key-row">
                                  <input
                                    type="checkbox"
                                    checked={checked}
                                    onChange={() => toggleKey(c.id)}
                                  />
                                  <span className="mfa-key-row-body">
                                    <span className="mfa-key-row-label">
                                      {c.label}
                                    </span>
                                    <span className="mfa-key-row-id">{c.id}</span>
                                  </span>
                                </label>
                              </li>
                            );
                          })}
                        </ul>
                      </details>
                    );
                  })}
                </div>
              )}
            </section>
          </div>

          <footer className="mfa-editor-footer">
            <button
              type="button"
              className="btn-mfa-secondary"
              onClick={closeEditor}
              disabled={saving}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn-mfa-primary"
              onClick={saveApp}
              disabled={saving}
            >
              {saving ? "Saving…" : editingId ? "Save changes" : "Create app"}
            </button>
          </footer>
        </div>
      )}

      {tab === TAB_TENANT && (
        <>
          <div className="mfa-field" style={{ maxWidth: 420 }}>
            <label htmlFor="mfa-tenant">Tenant</label>
            <select
              id="mfa-tenant"
              value={tenantId}
              onChange={(e) => setTenantId(e.target.value)}
            >
              <option value="">— Select tenant —</option>
              {tenants.map((t) => (
                <option key={t.tenantId} value={t.tenantId}>
                  {t.tenantName || t.tenantCode}
                </option>
              ))}
            </select>
          </div>
          {tenantId && loadingTenant ? (
            <PageLoading variant="compact" message="Loading tenant apps…" />
          ) : tenantId ? (
            <>
              <div className="mfa-tenant-always-panel">
                <h4 className="mfa-tenant-always-heading">
                  Always included for every tenant
                </h4>
                <p className="mfa-tenant-always-lead">
                  These are always available and cannot be turned off by app assignment.
                  When you use &quot;Only pages in these apps&quot; below, you are only
                  restricting <em>module</em> pages — not Home, Settings, or the module
                  launcher.
                </p>
                <ul className="mfa-tenant-always-checks">
                  <li>
                    <label className="mfa-tenant-always-item">
                      <input type="checkbox" checked disabled />
                      <span>
                        <strong>Home</strong>
                        <span className="mfa-muted"> — tenant dashboard</span>
                      </span>
                    </label>
                  </li>
                  <li>
                    <label className="mfa-tenant-always-item">
                      <input type="checkbox" checked disabled />
                      <span>
                        <strong>All modules</strong>
                        <span className="mfa-muted">
                          {" "}
                          — search / launcher (⌘K) to open any licensed page
                        </span>
                      </span>
                    </label>
                  </li>
                  <li>
                    <label className="mfa-tenant-always-item">
                      <input type="checkbox" checked disabled />
                      <span>
                        <strong>Settings</strong>
                        <span className="mfa-muted">
                          {" "}
                          — all {settingsPageCount} settings pages (company, masters, users,
                          plants, etc.)
                        </span>
                      </span>
                    </label>
                  </li>
                </ul>
                <details className="mfa-tenant-settings-details">
                  <summary>
                    View Settings page names ({settingsPageCount})
                  </summary>
                  <ul className="mfa-tenant-settings-list">
                    {SETTINGS_MENU_ITEMS_RAW.map((s) => (
                      <li key={s.key}>
                        <span className="mfa-tenant-settings-label">{s.label}</span>
                        <code className="mfa-tenant-settings-key">{s.key}</code>
                      </li>
                    ))}
                  </ul>
                </details>
              </div>

              <div className="tenant-app-grid" style={{ marginBottom: "1rem" }}>
                <label>
                  <input
                    type="radio"
                    name="tenant-scope"
                    checked={tenantScopeMode === "inherit"}
                    onChange={() => setTenantScopeMode("inherit")}
                  />
                  <span>
                    <strong>All licensed modules</strong>{" "}
                    <span className="mfa-muted">
                      (no app-based filtering — default)
                    </span>
                  </span>
                </label>
                <label>
                  <input
                    type="radio"
                    name="tenant-scope"
                    checked={tenantScopeMode === "custom"}
                    onChange={() => setTenantScopeMode("custom")}
                  />
                  <span>
                    <strong>Limit workspace to these apps only</strong>{" "}
                    <span className="mfa-muted">
                      (Home, All modules, and Settings stay on — see above)
                    </span>
                  </span>
                </label>
              </div>
              {tenantScopeMode === "custom" && (
                <div className="tenant-app-grid">
                  {activeApps.length === 0 ? (
                    <p className="mfa-muted">
                      No active app definitions. Create apps in the first tab.
                    </p>
                  ) : (
                    activeApps.map((a) => (
                      <label key={a.navAppId}>
                        <input
                          type="checkbox"
                          checked={selectedCodes.has(a.code)}
                          onChange={() => toggleTenantCode(a.code)}
                        />
                        <span>
                          <strong>{a.name}</strong>{" "}
                          <span className="mfa-muted">({a.code})</span>
                        </span>
                      </label>
                    ))
                  )}
                </div>
              )}
              <div className="mfa-toolbar" style={{ marginTop: "1rem" }}>
                <button
                  type="button"
                  className="btn-mfa-primary"
                  onClick={saveTenantApps}
                  disabled={saving}
                >
                  {saving ? "Saving…" : "Save tenant apps"}
                </button>
                <button
                  type="button"
                  className="btn-mfa-secondary"
                  onClick={clearTenantApps}
                  disabled={saving}
                >
                  Clear assignment
                </button>
              </div>
            </>
          ) : null}
        </>
      )}
    </div>
  );
};

export default ManageFeatureApps;
